import api from '../../../../../../api';
import { IFinancial } from '../../../../../../data/entities/customer/financial';
import { IEventV2 } from '../../../../../../data/entities/event';
import {
	IEventNote,
	IPendingEventNote,
} from '../../../../../../data/entities/event/event-note';
import { IExperiments } from '../../../../../../data/entities/experiments';
import { IPendingPostPayment } from '../../../../../../data/entities/post-payment';
import { toFloyd } from '../../../../../../entities/pricing/helpers';
import {
	DateFormats,
	getFullDateUtc,
} from '../../../../../../view/components/date-picker/helpers';

const HOST_CANCELLATION_DEFAULT_FEE_FLOYDIAN = Number(toFloyd(160));
const HOST_CANCELLATION_DEFAULT_CURRENCY = 'GBP';
const HOST_CANCELLATION_EP_DESCRIPTION = 'Host Cancellation Charge (Automated)';
const HOST_CANCELLATION_CHARGE_DESCRIPTION =
	'Travelnest Host Cancellation Charge';

export const isChargeHostCancellationEnabled = (
	event: IEventV2,
	customerExperiments: IExperiments
) => {
	const wasHostChargedForCancellation = event.booking?.post_payments?.some(
		(postPayment) =>
			postPayment.reason?.includes(HOST_CANCELLATION_EP_DESCRIPTION)
	);

	const isBookingCancelled = event.status === 'cancelled';

	const customerInExperiment =
		customerExperiments?.host_cancellation_payment === 'on';

	return (
		isBookingCancelled &&
		!wasHostChargedForCancellation &&
		customerInExperiment
	);
};

export const chargeHostCancellation = async (
	event: IEventV2,
	customerId: string,
	financial: IFinancial,
	submitPostPayments: (
		propertyId: string,
		eventId: string,
		postPayments: IPendingPostPayment[]
	) => any,
	submitEventNotes: (
		propertyId: string,
		eventId: string,
		eventNotes: IPendingEventNote[]
	) => any
) => {
	// Add event note
	const username = (await api.authentication.getCurrentUser()).getUsername();
	const eventNote = {
		action: 'add',
		eventNote: {
			event_id: event.id,
			note_type: 'audit',
			content: `${username} requested a host cancellation payment`,
		} as IEventNote,
		eventId: event.id,
	} as IPendingEventNote;
	await submitEventNotes(event.property_id, event.id, [eventNote]);

	// Add exceptional payment\
	// We always show this as the default fee and the booking fee model works out the changes
	await submitPostPayments(event.property_id, event.id, [
		{
			action: 'add',
			payment: {
				affects_commission: false,
				affects_tbv: false,
				amount: HOST_CANCELLATION_DEFAULT_FEE_FLOYDIAN,
				currency: HOST_CANCELLATION_DEFAULT_CURRENCY,
				direction: 'debit',
				target: 'owner',
				reason: HOST_CANCELLATION_EP_DESCRIPTION,
				taxable: true,
				created_at: new Date().toISOString(),
			},
			eventId: event.id,
		},
	]);

	// Try to charge the card
	// The VAT/Exchange rate logic is done in booking_fee/availability but needs to be duplicated here
	// So that we send the right amount to be charged
	// This means the host is charged the exact same as what they see on the booking page
	try {
		let price = HOST_CANCELLATION_DEFAULT_FEE_FLOYDIAN;
		const currency = event.booking?.value?.currency || 'GBP';

		// If the host is not priced in GBP, we need to convert the price to their currency
		if (currency !== HOST_CANCELLATION_DEFAULT_CURRENCY) {
			const today = getFullDateUtc(new Date(), DateFormats.YearMonthDay);
			const fxRate = await api.fx.getFXRateOnDate(
				HOST_CANCELLATION_DEFAULT_CURRENCY,
				currency,
				today
			);
			price = Math.round(price * fxRate);
		}

		let tax: { percentage: number; name: string } | undefined;
		// If the host is from the UK (or we don't know) we need to charge VAT
		if (
			!financial.correspondence_country_code ||
			financial.correspondence_country_code.toUpperCase() === 'GB'
		) {
			tax = {
				percentage:
					(event.booking?.value?.breakdown?.travelnest?.vat_rate ||
						0) * 100,
				name: 'VAT',
			};
		}

		const hostPaymentDetails = await api.hostPayments.searchPaymentDetails(
			customerId
		);
		if (hostPaymentDetails && Object.keys(hostPaymentDetails).length > 0)  {
			await api.hostPayments.chargeHostCancellation(
				customerId,
				event.id,
				price,
				currency,
				HOST_CANCELLATION_CHARGE_DESCRIPTION,
				tax
			);
		}
	} catch (e) {
		console.error('Error charging host cancellation');
	}

	window.location.reload();
};
